import { useParams, useNavigate } from "react-router-dom";
import { useFetch } from "../hooks/useFetch";
import { getDriverPaymentHistoryByAdmin } from "../api/subscriptionsApi";
import "./css/DriverHistory.css";

export default function DriverHistory() {
  const { driverId } = useParams();
  const navigate     = useNavigate();

  const { data, loading, error } = useFetch(
    () => getDriverPaymentHistoryByAdmin(driverId),
    [driverId]
  );

  /* Safely extract arrays from API response */
  const payments      = data?.payments ?? [];
  const subscriptions = data?.subscriptions ?? [];
  const driver        = payments[0]?.driverId;

  /* Calculate total paid */
  const totalPaid = payments
    .filter((p) => p.status === "paid")
    .reduce((sum, p) => sum + (p.amount || 0), 0);

  const getBadgeClass = (val) => {
    const map = {
      active:  "dh-badge dh-badge-active",
      expired: "dh-badge dh-badge-expired",
      pending: "dh-badge dh-badge-pending",
      paid:    "dh-badge dh-badge-paid",
      failed:  "dh-badge dh-badge-failed",
      online:  "dh-badge dh-badge-online",
      cash:    "dh-badge dh-badge-cash",
    };
    return map[val] || "dh-badge dh-badge-default";
  };

  return (
    <div className="dh-container">
      {/* Back */}
      <button className="dh-back" onClick={() => navigate(-1)}>
        ← Back
      </button>

      {loading ? (
        <div className="dh-skeleton">
          <div className="dh-skeleton-header" />
          <div className="dh-skeleton-sub" />
          <div className="dh-skeleton-cards">
            <div className="dh-skeleton-card" />
            <div className="dh-skeleton-card" />
          </div>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="dh-skeleton-row" />
          ))}
        </div>
      ) : error ? (
        <p className="dh-error">{error}</p>
      ) : (
        <>
          {/* Header */}
          <div className="dh-header">
            <h1>{driver?.name || "Driver"}</h1>
            <p>{driver?.phone || driverId}</p>
          </div>

          {/* Summary Cards */}
          <div className="dh-summary">
            <div className="dh-stat-card blue">
              <p className="dh-stat-value">₹{totalPaid.toLocaleString()}</p>
              <p className="dh-stat-label">Total Paid</p>
            </div>
            <div className="dh-stat-card green">
              <p className="dh-stat-value">{subscriptions.length}</p>
              <p className="dh-stat-label">Subscriptions</p>
            </div>
          </div>

          {/* Subscription History */}
          <h2 className="dh-section-title">Subscription History</h2>
          {subscriptions.length === 0 ? (
            <div className="dh-table-card">
              <p className="dh-table-empty">No subscriptions found</p>
            </div>
          ) : (
            <div className="dh-table-card">
              <table className="dh-table">
                <thead>
                  <tr>
                    <th>Plan</th>
                    <th>Start</th>
                    <th>End</th>
                    <th>Status</th>
                    <th>Renewed</th>
                  </tr>
                </thead>
                <tbody>
                  {subscriptions.map((r) => (
                    <tr key={r._id}>
                      <td>
                        <span className={getBadgeClass(r.planId?.name)}>
                          {r.planId?.name || "—"}
                        </span>
                      </td>
                      <td>{new Date(r.startDate).toLocaleDateString("en-IN")}</td>
                      <td>{new Date(r.endDate).toLocaleDateString("en-IN")}</td>
                      <td>
                        <span className={getBadgeClass(r.status)}>{r.status}</span>
                      </td>
                      <td>
                        {r.renewedFrom
                          ? <span className="dh-renewed-yes">Yes</span>
                          : <span className="dh-muted">—</span>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Payment History */}
          <h2 className="dh-section-title mt">Payment History</h2>
          {payments.length === 0 ? (
            <div className="dh-table-card">
              <p className="dh-table-empty">No payments found</p>
            </div>
          ) : (
            <div className="dh-table-card">
              <table className="dh-table">
                <thead>
                  <tr>
                    <th>Plan</th>
                    <th>Amount</th>
                    <th>Method</th>
                    <th>Status</th>
                    <th>Collected By</th>
                    <th>Paid At</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((r) => (
                    <tr key={r._id}>
                      <td>
                        <span className={getBadgeClass(r.planId?.name)}>
                          {r.planId?.name || "—"}
                        </span>
                      </td>
                      <td>
                        <span className="dh-amount">₹{r.amount?.toLocaleString()}</span>
                      </td>
                      <td>
                        <span className={getBadgeClass(r.method)}>{r.method}</span>
                      </td>
                      <td>
                        <span className={getBadgeClass(r.status)}>{r.status}</span>
                      </td>
                      <td>
                        {r.collectedBy?.name || <span className="dh-muted">—</span>}
                      </td>
                      <td>
                        {r.paidAt
                          ? new Date(r.paidAt).toLocaleString("en-IN")
                          : <span className="dh-muted">Pending</span>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
